import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { 
  Tabs, 
  TabsList, 
  TabsTrigger, 
  TabsContent 
} from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  UserPlus, 
  Building2, 
  Search, 
  Users, 
  FileUp, 
  Link, 
  MoreHorizontal,
  Edit,
  Trash,
  User,
  Key,
  Plus,
  UserRound,
  Info,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { formatDate, getInitials } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { EmptyState } from "@/components/ui/empty-state";
import { EmployeesTableWithPagination } from "@/components/ui/employees-table-with-pagination";
import { TeamsTableWithPagination } from "@/components/ui/teams-table-with-pagination";
import AssignLicenseDialog from "@/components/people/assign-license-dialog";
import PeopleImportDialog from "@/components/people/people-import-dialog";
import ViewEmployeeLicensesDialog from "@/components/people/view-employee-licenses-dialog";
import ViewTeamLicensesDialog from "@/components/people/view-team-licenses-dialog";
import EditEmployeeDialog from "@/components/people/edit-employee-dialog";
import EditTeamDialog from "@/components/people/edit-team-dialog";
import ManageTeamMembersDialog from "@/components/people/manage-team-members-dialog";
import TeamDetailsDialog from "@/components/people/team-details-dialog";
import EmployeeDetailsDialog from "@/components/people/employee-details-dialog";
import DeleteConfirmationDialog from "@/components/people/delete-confirmation-dialog";

export default function PeoplePage() {
  const [activeTab, setActiveTab] = useState<"employees" | "teams">("employees");
  const [searchQuery, setSearchQuery] = useState("");
  const [teamFilter, setTeamFilter] = useState("all");
  // Removed status filtering since all listed users are considered active
  
  // Pagination states
  const [employeesPage, setEmployeesPage] = useState(1);
  const [employeesPageSize, setEmployeesPageSize] = useState(10);
  const [teamsPage, setTeamsPage] = useState(1);
  const [teamsPageSize, setTeamsPageSize] = useState(10);
  
  // Dialog states
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showAssignLicenseDialog, setShowAssignLicenseDialog] = useState(false);
  const [showEmployeeLicensesDialog, setShowEmployeeLicensesDialog] = useState(false);
  const [showTeamLicensesDialog, setShowTeamLicensesDialog] = useState(false);
  const [showEditEmployeeDialog, setShowEditEmployeeDialog] = useState(false);
  const [showEditTeamDialog, setShowEditTeamDialog] = useState(false);
  const [showManageTeamMembersDialog, setShowManageTeamMembersDialog] = useState(false);
  const [showTeamDetailsDialog, setShowTeamDetailsDialog] = useState(false);
  const [showEmployeeDetailsDialog, setShowEmployeeDetailsDialog] = useState(false);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
  
  // Selected entity tracking
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
  const [selectedEntityName, setSelectedEntityName] = useState<string>("");

  // Define types for employees and teams
  type Employee = {
    id: number;
    fullName: string;
    email: string;
    jobTitle?: string;
    status?: string;
    licenseCount: number;
    teams?: { id: number; name: string }[];
  };

  type Team = {
    id: number;
    name: string;
    description?: string;
    licenseCount: number;
    memberCount?: number;
  };

  // Fetch employees
  const { 
    data: employees = [] as Employee[], 
    isLoading: isLoadingEmployees,
  } = useQuery<Employee[]>({
    queryKey: ['/api/employees'],
    enabled: activeTab === "employees",
  });

  // Fetch teams
  const { 
    data: teams = [] as Team[], 
    isLoading: isLoadingTeams,
  } = useQuery<Team[]>({
    queryKey: ['/api/teams'],
    enabled: activeTab === "teams",
  });

  // Filter employees based on search query and team filter (status filter removed)
  const filteredEmployees = employees.filter((employee: Employee) => {
    const matchesSearch = 
      (employee.fullName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (employee.email?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (employee.jobTitle?.toLowerCase() || '').includes(searchQuery.toLowerCase());
    
    const matchesTeam = 
      teamFilter === "all" || 
      (employee.teams && employee.teams.some(team => team.id.toString() === teamFilter));
    
    // Debug logging for employee data
    console.log('🔍 Employee Debug:', {
      employee: {
        id: employee.id,
        fullName: employee.fullName,
        email: employee.email,
        licenseCount: employee.licenseCount,
        teams: employee.teams
      }
    });
    
    return matchesSearch && matchesTeam;
  });

  // Apply pagination to employees
  const employeesTotalPages = Math.ceil(filteredEmployees.length / employeesPageSize);
  const paginatedEmployees = filteredEmployees.slice(
    (employeesPage - 1) * employeesPageSize,
    employeesPage * employeesPageSize
  );

  // Filter teams based on search query
  const filteredTeams = teams.filter((team: Team) => {
    const matchesSearch = 
      (team.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (team.description?.toLowerCase() || '').includes(searchQuery.toLowerCase());
    
    // Debug logging
    if (searchQuery && searchQuery.length > 0) {
      console.log('🔍 Team Search Debug:', {
        searchQuery,
        team: {
          name: team.name,
          description: team.description
        },
        matchesSearch,
        result: matchesSearch
      });
    }
    
    return matchesSearch;
  });
  
  // Apply pagination to teams
  const teamsTotalPages = Math.ceil(filteredTeams.length / teamsPageSize);
  const paginatedTeams = filteredTeams.slice(
    (teamsPage - 1) * teamsPageSize,
    teamsPage * teamsPageSize
  );

  // Get all teams for filtering
  const teamsForFilter = teams.map(team => ({
    id: team.id.toString(),
    name: team.name
  }));

  // Removed employee status options since all listed users are considered active

  // Handle opening the assign license dialog
  const handleAssignLicense = (id: number) => {
    setSelectedEntityId(id);
    setShowAssignLicenseDialog(true);
  };

  // Handle viewing employee licenses
  const handleViewEmployeeLicenses = (employee: Employee) => {
    setSelectedEntityId(employee.id);
    setSelectedEntityName(employee.fullName);
    setShowEmployeeLicensesDialog(true);
  };

  // Handle viewing team licenses
  const handleViewTeamLicenses = (team: Team) => {
    setSelectedEntityId(team.id);
    setSelectedEntityName(team.name);
    setShowTeamLicensesDialog(true);
  };

  // Handle editing an employee
  const handleEditEmployee = (employee: Employee) => {
    setSelectedEntityId(employee.id);
    setSelectedEntityName(employee.fullName);
    setShowEditEmployeeDialog(true);
  };

  // Handle editing a team
  const handleEditTeam = (team: Team) => {
    setSelectedEntityId(team.id);
    setSelectedEntityName(team.name);
    setShowEditTeamDialog(true);
  };

  // Handle managing team members
  const handleManageTeamMembers = (team: Team) => {
    setSelectedEntityId(team.id);
    setSelectedEntityName(team.name);
    setShowManageTeamMembersDialog(true);
  };
  
  // Handle viewing team details
  const handleViewTeamDetails = (team: Team) => {
    setSelectedEntityId(team.id);
    setSelectedEntityName(team.name);
    setShowTeamDetailsDialog(true);
  };


  
  // Handle viewing employee details
  const handleViewEmployeeDetails = (employee: Employee) => {
    setSelectedEntityId(employee.id);
    setSelectedEntityName(employee.fullName);
    setShowEmployeeDetailsDialog(true);
  };

  // Handle deleting an entity (employee or team)
  const handleDeleteEntity = (id: number, name: string, type: "employee" | "team") => {
    setSelectedEntityId(id);
    setSelectedEntityName(name);
    setShowDeleteConfirmationDialog(true);
  };

  // Handle creating a new employee
  const handleCreateEmployee = () => {
    setSelectedEntityId(null);
    setShowEditEmployeeDialog(true);
  };

  // Handle creating a new team
  const handleCreateTeam = () => {
    setSelectedEntityId(null);
    setSelectedEntityName("");
    setShowEditTeamDialog(true);
  };
  
  // Handle post-team creation member management
  const handlePostTeamCreation = (teamId: number, teamName: string) => {
    setSelectedEntityId(teamId);
    setSelectedEntityName(teamName);
    setShowManageTeamMembersDialog(true);
  };

  return (
    <>
      <Helmet>
        <title>Employees | SaaSGama</title>
      </Helmet>
      
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Employees</h1>
            <p className="text-slate-500">
              Manage your employees and teams for license assignments
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowImportDialog(true)}
              className="h-9"
            >
              <FileUp className="h-4 w-4 mr-2" />
              Import Employees
            </Button>
            
            <Button
              onClick={() => {
                // Handle adding new employee or team based on active tab
                if (activeTab === "employees") {
                  handleCreateEmployee();
                } else {
                  handleCreateTeam();
                }
              }}
              className="h-9"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add {activeTab === "employees" ? "Employee" : "Team"}
            </Button>
          </div>
        </div>
        
        <Tabs 
          defaultValue="employees" 
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as "employees" | "teams")}
          className="w-full"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <TabsList className="h-9 w-full sm:w-auto">
              <TabsTrigger value="employees" className="px-2 sm:px-4 flex-1 sm:flex-none">
                <UserRound className="h-4 w-4 mr-2" />
                Employees
              </TabsTrigger>
              <TabsTrigger value="teams" className="px-2 sm:px-4 flex-1 sm:flex-none">
                <Users className="h-4 w-4 mr-2" />
                Teams
              </TabsTrigger>
            </TabsList>
            
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
                <Input
                  placeholder={`Search ${activeTab}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9 w-full sm:w-[200px] lg:w-[300px]"
                />
              </div>
              
              {activeTab === "employees" && (
                <Select 
                  value={teamFilter}
                  onValueChange={setTeamFilter}
                >
                  <SelectTrigger className="h-9 w-full sm:w-[180px]">
                    <SelectValue placeholder="Team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Teams</SelectItem>
                    {teamsForFilter.map((team) => (
                      <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {/* Status filter removed since all listed users are considered active */}
            </div>
          </div>
          
          <TabsContent value="employees" className="m-0">
            <Card>
              <CardContent className="p-0">
                <EmployeesTableWithPagination
                  employees={employees}
                  filteredEmployees={filteredEmployees}
                  paginatedEmployees={paginatedEmployees}
                  isLoading={isLoadingEmployees}
                  page={employeesPage}
                  pageSize={employeesPageSize}
                  totalPages={employeesTotalPages}
                  onPageChange={setEmployeesPage}
                  onPageSizeChange={setEmployeesPageSize}
                  onViewDetails={handleViewEmployeeDetails}
                  onViewLicenses={handleViewEmployeeLicenses}
                  onEdit={handleEditEmployee}

                  onDelete={(id, name) => handleDeleteEntity(id, name, "employee")}
                  onAssignLicense={handleAssignLicense}
                  onCreateEmployee={handleCreateEmployee}
                  onImport={() => setShowImportDialog(true)}
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="teams" className="m-0">
            <Card>
              <CardContent className="p-0">
                <TeamsTableWithPagination
                  teams={teams}
                  filteredTeams={filteredTeams}
                  paginatedTeams={paginatedTeams}
                  isLoading={isLoadingTeams}
                  page={teamsPage}
                  pageSize={teamsPageSize}
                  totalPages={teamsTotalPages}
                  onPageChange={setTeamsPage}
                  onPageSizeChange={setTeamsPageSize}
                  onViewDetails={handleViewTeamDetails}
                  onViewLicenses={handleViewTeamLicenses}
                  onEdit={handleEditTeam}
                  onManageMembers={handleManageTeamMembers}
                  onDelete={(id, name) => handleDeleteEntity(id, name, "team")}
                  onAssignLicense={handleAssignLicense}
                  onCreateTeam={handleCreateTeam}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* People Import Dialog - Employee Only */}
      <PeopleImportDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
      />
      
      {/* Assign License Dialog */}
      {selectedEntityId && (
        <AssignLicenseDialog
          open={showAssignLicenseDialog}
          onOpenChange={setShowAssignLicenseDialog}
          entityType={activeTab === "employees" ? "employee" : "team"}
          entityId={selectedEntityId}
        />
      )}

      {/* View Employee Licenses Dialog */}
      <ViewEmployeeLicensesDialog
        employeeId={selectedEntityId}
        employeeName={selectedEntityName}
        isOpen={showEmployeeLicensesDialog}
        onClose={() => setShowEmployeeLicensesDialog(false)}
      />

      {/* View Team Licenses Dialog */}
      <ViewTeamLicensesDialog
        teamId={selectedEntityId}
        teamName={selectedEntityName}
        isOpen={showTeamLicensesDialog}
        onClose={() => setShowTeamLicensesDialog(false)}
      />

      {/* Edit Employee Dialog */}
      <EditEmployeeDialog
        employeeId={selectedEntityId}
        isOpen={showEditEmployeeDialog}
        onClose={() => setShowEditEmployeeDialog(false)}
      />

      {/* Edit Team Dialog */}
      <EditTeamDialog
        teamId={selectedEntityId}
        isOpen={showEditTeamDialog}
        onClose={() => setShowEditTeamDialog(false)}
        onManageMembers={handlePostTeamCreation}
      />

      {/* Manage Team Members Dialog */}
      <ManageTeamMembersDialog
        teamId={selectedEntityId}
        teamName={selectedEntityName}
        isOpen={showManageTeamMembersDialog}
        onClose={() => setShowManageTeamMembersDialog(false)}
      />

      {/* Team Details Dialog */}
      <TeamDetailsDialog
        teamId={selectedEntityId}
        isOpen={showTeamDetailsDialog}
        onClose={() => setShowTeamDetailsDialog(false)}
        onManageMembers={(teamId, teamName) => {
          setSelectedEntityId(teamId);
          setSelectedEntityName(teamName);
          setShowTeamDetailsDialog(false);
          setShowManageTeamMembersDialog(true);
        }}
      />

      {/* Employee Details Dialog */}
      <EmployeeDetailsDialog
        employeeId={selectedEntityId}
        isOpen={showEmployeeDetailsDialog}
        onClose={() => setShowEmployeeDetailsDialog(false)}
        onAssignLicense={(employeeId, employeeName) => {
          setSelectedEntityId(employeeId);
          setSelectedEntityName(employeeName);
          setShowEmployeeDetailsDialog(false);
          setShowAssignLicenseDialog(true);
        }}

      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        entityId={selectedEntityId}
        entityName={selectedEntityName}
        entityType={activeTab === "employees" ? "employee" : "team"}
        isOpen={showDeleteConfirmationDialog}
        onClose={() => setShowDeleteConfirmationDialog(false)}
      />
    </>
  );
}